/**
 * Stable element handles (Cluster C items 26, 27).
 *
 * Selectors break the moment the customer's design system renames a class
 * or restructures a wrapper div. Handles survive most of those changes
 * because they hash by the element's *intent* (role, accessible name,
 * form-group context) rather than its position in the DOM.
 *
 * Lifecycle:
 *   1. On iframe boot, `tagInteractiveElements()` walks the document and
 *      writes `data-iraa-id="<hash>"` on every element that matches the
 *      interactive-selectors set. Idempotent — re-running on mutation
 *      preserves existing tags.
 *   2. The element-mapper includes `data-iraa-id` in every snapshot entry.
 *   3. Tools accept either a `selector` or a `handle` parameter. Handles
 *      look up via `[data-iraa-id="..."]` which is stable across class
 *      renames and DOM restructures.
 *   4. `computeSnapshotHash()` rolls all handles into a single hash; the
 *      server stores it on every PDP element. Mismatched hashes are
 *      surfaced as `selector_versioning_drift` audit events instead of
 *      silent fallback healing — authors get a real signal that the PDP
 *      is stale (item 26).
 */

const TAG_ATTR = 'data-iraa-id';

/**
 * Cheap deterministic 32-bit hash. Not cryptographic — collision rate is
 * fine for tagging at scale (<= 10K interactive elements per page).
 */
function fnv1a(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

/**
 * Stable signature for an element. Built from intent-bearing attributes
 * that rarely change across deploys: role, accessible name, name attr,
 * form-group label, position-within-form-group. Excludes ephemeral things
 * like generated CSS class names, computed positions, BEM modifiers.
 */
function elementSignature(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute('role') ?? '';
  const aria = el.getAttribute('aria-label') ?? '';
  const name = el.getAttribute('name') ?? '';
  const type = el.getAttribute('type') ?? '';
  const placeholder = el.getAttribute('placeholder') ?? '';
  const text = (el as HTMLElement).innerText?.trim().slice(0, 40) ?? '';

  // Form-group context: label associated by `for=` or by being inside the
  // same form group / fieldset. Captures "the email field on the signup
  // form" vs "the email field on the change-email modal".
  let groupLabel = '';
  if (el instanceof HTMLInputElement && el.id) {
    const lbl = el.ownerDocument?.querySelector(`label[for="${CSS.escape(el.id)}"]`);
    if (lbl) groupLabel = lbl.textContent?.trim().slice(0, 40) ?? '';
  }

  return `${tag}|${role}|${aria || text}|${name}|${type}|${placeholder}|${groupLabel}`;
}

const INTERACTIVE_SELECTOR =
  'a[href],button,input,textarea,select,[role="button"],[role="link"],[role="tab"],[role="menuitem"],[role="option"],[role="checkbox"],[role="radio"],[contenteditable="true"]';

/**
 * Walk the document and tag every interactive element with a stable handle.
 * Idempotent — preserves existing tags on re-run after DOM mutations.
 */
export function tagInteractiveElements(root: ParentNode = document): number {
  let tagged = 0;
  const all = root.querySelectorAll(INTERACTIVE_SELECTOR);
  all.forEach((el) => {
    if (el.hasAttribute(TAG_ATTR)) return;
    const sig = elementSignature(el);
    if (!sig.replace(/\|/g, '').trim()) return; // empty signature → skip
    el.setAttribute(TAG_ATTR, fnv1a(sig));
    tagged++;
  });
  return tagged;
}

/**
 * Lookup by handle. Faster than a CSS selector match (single attribute
 * query) and survives class renames / wrapper reshuffles.
 */
export function findByHandle(handle: string, root: ParentNode = document): Element | null {
  if (!handle) return null;
  return root.querySelector(`[${TAG_ATTR}="${CSS.escape(handle)}"]`);
}

/**
 * Roll up every handle in the document into a single fingerprint.
 * Stored alongside each PDP screen at authoring time; the runtime compares
 * to detect "the live product's handle set has shifted from what the PDP
 * was authored against" — that's a structured drift signal authors can act
 * on, instead of waiting for selector misses to accumulate.
 */
export function computeSnapshotHash(root: ParentNode = document): string {
  const handles: string[] = [];
  root.querySelectorAll(`[${TAG_ATTR}]`).forEach((el) => {
    const h = el.getAttribute(TAG_ATTR);
    if (h) handles.push(h);
  });
  handles.sort();
  return fnv1a(handles.join(','));
}
